import { useTranslation } from "react-i18next";

import type { CalibrationPreview } from "@/api/calibration";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCount } from "@/lib/format";

export interface LiveResultProps {
  preview: CalibrationPreview | undefined;
  pending: boolean;
}

/**
 * O resultado ao vivo e a memória de cálculo.
 *
 * **A memória fica aqui, e não no Ato 5.** O critério da issue é que o Ato 5 não
 * mostre planilha de cálculo ao cliente. Esta é a tela de quem trabalha o
 * número, e é onde a conta tem de estar visível passo a passo.
 *
 * Passo que depende de premissa sai marcado. É o que deixa a tela dizer "34,1%
 * de economia, por premissa" em vez de "34,1% de economia": o número é o mesmo,
 * o que muda é o que ele autoriza alguém a afirmar.
 */
export function LiveResult({ preview, pending }: LiveResultProps) {
  const { t } = useTranslation();

  if (!preview) {
    return (
      <p className="text-sm text-muted-foreground">
        {pending
          ? t("Calculating…")
          : t("The estimate appears when the approved scope has items to activate.")}
      </p>
    );
  }

  const kpis = [
    { label: t("Effort (HH)"), value: formatCount(preview.total_hh) },
    { label: t("Person-months"), value: formatCount(preview.total_hm, { decimals: 1 }) },
    {
      label: t("Duration (months)"),
      value: formatCount(preview.duration_months, { decimals: 1 }),
    },
    { label: t("Savings"), value: `${formatCount(preview.savings_pct, { decimals: 1 })}%` },
  ];

  return (
    <div className="flex flex-col gap-4" aria-busy={pending}>
      <div className="grid grid-cols-2 gap-3">
        {kpis.map((kpi) => (
          <Card key={kpi.label}>
            <CardContent className="p-3">
              <div className="text-xl font-semibold tabular-nums">{kpi.value}</div>
              <div className="text-[11px] leading-tight text-muted-foreground">{kpi.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{t("Calculation memory")}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ul className="divide-y text-sm">
            {preview.memory.map((step) => (
              // A fórmula vai embaixo do rótulo, e não numa terceira coluna.
              // No painel lateral, que tem 28rem, três colunas deixavam a
              // fórmula com uns 8 caracteres: "soma(d…", "bruta × …". Memória
              // de cálculo truncada não é memória de cálculo.
              <li key={step.name} className="px-4 py-2">
                <div className="flex items-baseline gap-3">
                  <span className="min-w-0 flex-1">{step.label}</span>
                  <span className="shrink-0 text-right font-medium tabular-nums">
                    {formatCount(step.value, { decimals: 2 })}
                  </span>
                  {step.from_hypothesis ? (
                    <Badge
                      variant="outline"
                      className="shrink-0 text-[10px]"
                      title={t("Declared assumption, not a measurement")}
                    >
                      {t("assumption")}
                    </Badge>
                  ) : null}
                </div>
                {step.formula ? (
                  <div className="mt-0.5 break-words text-xs text-muted-foreground">
                    {step.formula}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
